import { Contact } from '@/infra/types/domain';

// ─── State ────────────────────────────────────────────────

export interface ContactsState {
  contacts: Contact[];
  searchResults: Contact[];
  loading: boolean;
  searchLoading: boolean;
  error: string | null;
}

const initialState: ContactsState = {
  contacts: [],
  searchResults: [],
  loading: false,
  searchLoading: false,
  error: null,
};

// ─── Action Types ─────────────────────────────────────────

export const FETCH_CONTACTS = 'contacts/FETCH_CONTACTS';
export const SET_CONTACTS = 'contacts/SET_CONTACTS';
export const SEARCH_CONTACTS = 'contacts/SEARCH_CONTACTS';
export const SET_SEARCH_RESULTS = 'contacts/SET_SEARCH_RESULTS';
export const CLEAR_SEARCH = 'contacts/CLEAR_SEARCH';
export const CREATE_CONTACT = 'contacts/CREATE_CONTACT';
export const ADD_CONTACT = 'contacts/ADD_CONTACT';
export const ENABLE_CONTACT = 'contacts/ENABLE_CONTACT';
export const DISABLE_CONTACT = 'contacts/DISABLE_CONTACT';
export const REMOVE_CONTACT = 'contacts/REMOVE_CONTACT';
export const UPDATE_CONTACT_ENABLED = 'contacts/UPDATE_CONTACT_ENABLED';
export const UPDATE_CONTACT = 'contacts/UPDATE_CONTACT';
export const SET_CONTACT = 'contacts/SET_CONTACT';
export const ENHANCE_CONTACT = 'contacts/ENHANCE_CONTACT';
export const UPDATE_ROLE = 'contacts/UPDATE_ROLE';
export const SET_LOADING = 'contacts/SET_LOADING';
export const SET_SEARCH_LOADING = 'contacts/SET_SEARCH_LOADING';
export const SET_ERROR = 'contacts/SET_ERROR';

// ─── Action Creators ──────────────────────────────────────

export const fetchContacts = () => ({ type: FETCH_CONTACTS } as const);
export const setContacts = (contacts: Contact[]) => ({ type: SET_CONTACTS, payload: contacts } as const);
export const searchContactsAction = (query: string) => ({ type: SEARCH_CONTACTS, payload: query } as const);
export const setSearchResults = (results: Contact[]) => ({ type: SET_SEARCH_RESULTS, payload: results } as const);
export const clearSearch = () => ({ type: CLEAR_SEARCH } as const);
export const createContactAction = (data: { name: string; phone?: string; email?: string; role?: string }) =>
  ({ type: CREATE_CONTACT, payload: data } as const);
export const addContact = (contact: Contact) => ({ type: ADD_CONTACT, payload: contact } as const);
export const enableContactAction = (id: string, role?: string) =>
  ({ type: ENABLE_CONTACT, payload: { id, role } } as const);
export const disableContactAction = (id: string) => ({ type: DISABLE_CONTACT, payload: id } as const);
export const removeContact = (id: string) => ({ type: REMOVE_CONTACT, payload: id } as const);
export const updateContactEnabled = (id: string, enabled: boolean) =>
  ({ type: UPDATE_CONTACT_ENABLED, payload: { id, enabled } } as const);
export const updateContactAction = (id: string, data: Partial<{ display_name: string; first_name: string; last_name: string; organization: string; notes: string; website: string; role: string; primary_phone: string; primary_email: string }>) =>
  ({ type: UPDATE_CONTACT, payload: { id, data } } as const);
export const setContact = (contact: Contact) => ({ type: SET_CONTACT, payload: contact } as const);
export const enhanceContactAction = (id: string) => ({ type: ENHANCE_CONTACT, payload: id } as const);
export const updateRoleAction = (id: string, role: string) =>
  ({ type: UPDATE_ROLE, payload: { id, role } } as const);
export const setLoading = (loading: boolean) => ({ type: SET_LOADING, payload: loading } as const);
export const setSearchLoading = (loading: boolean) => ({ type: SET_SEARCH_LOADING, payload: loading } as const);
export const setError = (error: string | null) => ({ type: SET_ERROR, payload: error } as const);

type ContactsAction =
  | ReturnType<typeof fetchContacts>
  | ReturnType<typeof setContacts>
  | ReturnType<typeof searchContactsAction>
  | ReturnType<typeof setSearchResults>
  | ReturnType<typeof clearSearch>
  | ReturnType<typeof createContactAction>
  | ReturnType<typeof addContact>
  | ReturnType<typeof enableContactAction>
  | ReturnType<typeof disableContactAction>
  | ReturnType<typeof removeContact>
  | ReturnType<typeof updateContactEnabled>
  | ReturnType<typeof updateContactAction>
  | ReturnType<typeof setContact>
  | ReturnType<typeof enhanceContactAction>
  | ReturnType<typeof updateRoleAction>
  | ReturnType<typeof setLoading>
  | ReturnType<typeof setSearchLoading>
  | ReturnType<typeof setError>;

// ─── Reducer ──────────────────────────────────────────────

export function contactsReducer(state: ContactsState = initialState, action: ContactsAction): ContactsState {
  switch (action.type) {
    case SET_CONTACTS:
      return { ...state, contacts: action.payload, loading: false, error: null };
    case SET_SEARCH_RESULTS:
      return { ...state, searchResults: action.payload, searchLoading: false };
    case CLEAR_SEARCH:
      return { ...state, searchResults: [] };
    case ADD_CONTACT:
      return { ...state, contacts: [action.payload, ...state.contacts] };
    case SET_CONTACT: {
      const updated = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((c) => c.id === updated.id ? updated : c),
        searchResults: state.searchResults.map((c) => c.id === updated.id ? updated : c),
      };
    }
    case REMOVE_CONTACT:
      return { ...state, contacts: state.contacts.filter((c) => c.id !== action.payload) };
    case UPDATE_CONTACT_ENABLED: {
      const updateEnabled = (list: Contact[]) =>
        list.map((c) => c.id === action.payload.id ? { ...c, enabled: action.payload.enabled } : c);
      return {
        ...state,
        contacts: updateEnabled(state.contacts),
        searchResults: updateEnabled(state.searchResults),
      };
    }
    case SET_LOADING:
      return { ...state, loading: action.payload };
    case SET_SEARCH_LOADING:
      return { ...state, searchLoading: action.payload };
    case SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}
